import { useState, useEffect } from 'react';
import { Plus, ExternalLink, Trash2, Edit2, Activity, Power, PowerOff, Check, X, AlertCircle, Loader, Webhook as WebhookIcon, Save, Key, FileJson, GraduationCap, Settings2, Zap, ArrowLeft, ArrowRight, ChevronRight, Eye, Calendar, RefreshCw, RotateCcw, Search, BarChart3, Copy, Download } from 'lucide-react';
import { authService } from '../../../utils/auth/authService';
import webhookService, { Webhook, WebhookLog, LeadField, AuthType, PayloadFormat, WebhookConfig, WebhookAuth, WebhookStats } from '../../../services/webhookService';
import { toast } from 'react-toastify';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';

// Default grade keys for mapping
const GRADE_KEYS = [
  'pre_k', 'pre_primary_1', 'pre_primary_2', 'pre_primary_3',
  'grade_1', 'grade_2', 'grade_3', 'grade_4', 'grade_5', 'grade_6',
  'grade_7', 'grade_8', 'grade_9', 'grade_10', 'grade_11', 'grade_12'
];

// Step definitions
const STEPS = [
  { id: 'basic', title: 'Basic Info', icon: WebhookIcon, description: 'Name and URL' },
  { id: 'auth', title: 'Authentication', icon: Key, description: 'Security settings' },
  { id: 'payload', title: 'Payload Format', icon: FileJson, description: 'Data structure' },
  { id: 'fields', title: 'Field Mapping', icon: Zap, description: 'Map CRM fields' },
  { id: 'grades', title: 'Grade Mapping', icon: GraduationCap, description: 'Grade values' },
  { id: 'static', title: 'Static Fields', icon: Settings2, description: 'Fixed values' },
  { id: 'review', title: 'Review', icon: Eye, description: 'Review & save' }
];

function LeadSquaredInboundCard() {
  const [copied, setCopied] = useState(false);
  const user = authService.getUser();
  const clientUuid = user?.client_uuid;
  const leadIntakeUrl = import.meta.env.VITE_LEAD_INTAKE_URL || '';
  const webhookUrl = clientUuid ? `${leadIntakeUrl}/api/webhooks/leadsquared/${clientUuid}` : '';

  const handleCopy = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg flex-shrink-0">
          <Download className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Receive Leads from LeadSquared</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Paste this URL into LeadSquared → Automations → WebHook. Supports Lead.Create and Lead.Update events.
          </p>
        </div>
      </div>

      {clientUuid ? (
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300 truncate select-all">
            {webhookUrl}
          </div>
          <button
            onClick={handleCopy}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-brandColor text-white text-xs font-medium rounded-lg hover:bg-brand-hover transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      ) : (
        <p className="text-sm text-red-500">Unable to load webhook URL. Please refresh.</p>
      )}

      <div className="flex gap-2 mt-3">
        <span className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700">
          Lead.Create
        </span>
        <span className="inline-flex items-center px-2 py-0.5 text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-full border border-purple-200 dark:border-purple-700">
          Lead.Update
        </span>
      </div>
    </div>
  );
}

export default function WebhooksSettings() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsStartDate, setStatsStartDate] = useState('');
  const [statsEndDate, setStatsEndDate] = useState('');
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsStartDate, setLogsStartDate] = useState('');
  const [logsEndDate, setLogsEndDate] = useState('');
  const [logsSearch, setLogsSearch] = useState('');
  const [logsStatusFilter, setLogsStatusFilter] = useState('');
  const [resendingLogId, setResendingLogId] = useState<number | null>(null);
  const [availableFields, setAvailableFields] = useState<LeadField[]>([]);
  const [authTypes, setAuthTypes] = useState<AuthType[]>([]);
  const [payloadFormats, setPayloadFormats] = useState<PayloadFormat[]>([]);
  const [defaultGradeMappings, setDefaultGradeMappings] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    webhook_name: '',
    webhook_url: '',
    is_active: true,
    field_mapping: {} as Record<string, string>,
    config: {
      auth: { type: 'none' } as WebhookAuth,
      payload_format: 'json' as 'json' | 'leadsquared' | 'zoho_crm' | 'zoho_function' | 'custom' | 'form_data',
      grade_mapping: {} as Record<string, string>,
      static_fields: {} as Record<string, string>,
      default_values: {} as Record<string, string>,
      headers: {} as Record<string, string>,
      custom_template: '',
      phone_prefix: ''
    } as WebhookConfig
  });

  // New static field input state
  const [newStaticKey, setNewStaticKey] = useState('');
  const [newStaticValue, setNewStaticValue] = useState('');

  // New default value input state
  const [newDefaultKey, setNewDefaultKey] = useState('');
  const [newDefaultValue, setNewDefaultValue] = useState('');

  // New custom header input state
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [webhooksData, fieldsData, authTypesData, formatsData, gradeMappingsData] = await Promise.all([
        webhookService.getWebhooks(),
        webhookService.getAvailableFields(),
        webhookService.getAuthTypes(),
        webhookService.getPayloadFormats(),
        webhookService.getGradeMappings()
      ]);
      setWebhooks(webhooksData);
      setAvailableFields(fieldsData);
      setAuthTypes(authTypesData);
      setPayloadFormats(formatsData);
      setDefaultGradeMappings(gradeMappingsData);
    } catch (error: any) {
      toast.error('Failed to fetch webhook data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWizard = (webhook?: Webhook) => {
    if (webhook) {
      setSelectedWebhook(webhook);
      setFormData({
        webhook_name: webhook.webhook_name,
        webhook_url: webhook.webhook_url,
        is_active: webhook.is_active,
        field_mapping: webhook.field_mapping || {},
        config: {
          auth: webhook.config?.auth || { type: 'none' },
          payload_format: webhook.config?.payload_format || 'json',
          grade_mapping: webhook.config?.grade_mapping || {},
          static_fields: webhook.config?.static_fields || {},
          default_values: webhook.config?.default_values || {},
          headers: webhook.config?.headers || {},
          custom_template: webhook.config?.custom_template || '',
          phone_prefix: webhook.config?.phone_prefix || ''
        }
      });
    } else {
      setSelectedWebhook(null);
      setFormData({
        webhook_name: '',
        webhook_url: '',
        is_active: true,
        field_mapping: {},
        config: {
          auth: { type: 'none' },
          payload_format: 'json',
          grade_mapping: {},
          static_fields: {},
          default_values: {},
          headers: {},
          custom_template: '',
          phone_prefix: ''
        }
      });
    }
    setCurrentStep(0);
    setShowWizard(true);
  };

  const handleCloseWizard = () => {
    setShowWizard(false);
    setCurrentStep(0);
    setSelectedWebhook(null);
  };

  const handleSave = async () => {
    if (!formData.webhook_name || !formData.webhook_url) {
      toast.error('Webhook name and URL are required');
      setCurrentStep(0);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        webhook_name: formData.webhook_name,
        webhook_url: formData.webhook_url,
        is_active: formData.is_active,
        field_mapping: formData.field_mapping,
        config: formData.config
      };

      if (selectedWebhook) {
        await webhookService.updateWebhook(selectedWebhook.id, payload);
        toast.success('Webhook updated successfully');
      } else {
        await webhookService.createWebhook(payload);
        toast.success('Webhook created successfully');
      }
      handleCloseWizard();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (webhook: Webhook) => {
    try {
      await webhookService.updateWebhook(webhook.id, { is_active: !webhook.is_active });
      toast.success(`Webhook ${webhook.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const handleDelete = async () => {
    if (!selectedWebhook) return;

    try {
      await webhookService.deleteWebhook(selectedWebhook.id);
      toast.success('Webhook deleted successfully');
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleViewLogs = async (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setLogs([]);
    setLogsStartDate('');
    setLogsEndDate('');
    setLogsSearch('');
    setLogsStatusFilter('');
    setLogsLoading(true);
    setShowLogsModal(true);
    try {
      const { logs: webhookLogs, total, hasMore } = await webhookService.getWebhookLogs(webhook.id);
      setLogs(webhookLogs);
      setLogsTotal(total);
      setLogsHasMore(hasMore);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleFilterLogs = async (statusFilterOverride?: string) => {
    if (!selectedWebhook) return;
    setLogs([]);
    setLogsLoading(true);
    const statusFilter = statusFilterOverride !== undefined ? statusFilterOverride : logsStatusFilter;
    try {
      const { logs: webhookLogs, total, hasMore } = await webhookService.getWebhookLogs(
        selectedWebhook.id,
        50,
        0,
        logsStartDate || undefined,
        logsEndDate || undefined,
        logsSearch || undefined,
        statusFilter || undefined
      );
      setLogs(webhookLogs);
      setLogsTotal(total);
      setLogsHasMore(hasMore);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLoadMoreLogs = async () => {
    if (!selectedWebhook || logsLoading) return;
    setLogsLoading(true);
    try {
      const { logs: webhookLogs, total, hasMore } = await webhookService.getWebhookLogs(
        selectedWebhook.id,
        50,
        logs.length,
        logsStartDate || undefined,
        logsEndDate || undefined,
        logsSearch || undefined,
        logsStatusFilter || undefined
      );
      setLogs(prev => [...prev, ...webhookLogs]);
      setLogsTotal(total);
      setLogsHasMore(hasMore);
    } catch (error) {
      toast.error('Failed to load more logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleResendWebhook = async (logId: number) => {
    if (!selectedWebhook || resendingLogId) return;
    setResendingLogId(logId);
    try {
      const result = await webhookService.resendWebhook(selectedWebhook.id, logId);
      if (result.success) {
        toast.success('Webhook resent successfully');
        // Refresh the logs to show the new entry
        handleFilterLogs();
      } else {
        toast.error(result.message || 'Failed to resend webhook');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to resend webhook');
    } finally {
      setResendingLogId(null);
    }
  };

  const handleViewStats = async (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setStats(null);
    setStatsStartDate('');
    setStatsEndDate('');
    setStatsLoading(true);
    setShowStatsModal(true);
    try {
      const statsData = await webhookService.getWebhookStats(webhook.id);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterStats = async () => {
    if (!selectedWebhook) return;
    setStatsLoading(true);
    try {
      const statsData = await webhookService.getWebhookStats(
        selectedWebhook.id,
        statsStartDate || undefined,
        statsEndDate || undefined
      );
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const updateAuth = (updates: Partial<WebhookAuth>) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        auth: { ...prev.config.auth!, ...updates }
      }
    }));
  };

  const updateStaticField = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        static_fields: { ...prev.config.static_fields, [key]: value }
      }
    }));
  };

  const removeStaticField = (key: string) => {
    setFormData(prev => {
      const newFields = { ...prev.config.static_fields };
      delete newFields[key];
      return {
        ...prev,
        config: { ...prev.config, static_fields: newFields }
      };
    });
  };

  const addStaticField = () => {
    if (newStaticKey && newStaticValue) {
      updateStaticField(newStaticKey, newStaticValue);
      setNewStaticKey('');
      setNewStaticValue('');
    }
  };

  // Default value management functions
  const updateDefaultValue = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        default_values: { ...prev.config.default_values, [key]: value }
      }
    }));
  };

  const removeDefaultValue = (key: string) => {
    setFormData(prev => {
      const newDefaults = { ...prev.config.default_values };
      delete newDefaults[key];
      return {
        ...prev,
        config: { ...prev.config, default_values: newDefaults }
      };
    });
  };

  const addDefaultValue = () => {
    if (newDefaultKey && newDefaultValue) {
      updateDefaultValue(newDefaultKey, newDefaultValue);
      setNewDefaultKey('');
      setNewDefaultValue('');
    }
  };

  // Custom header management functions
  const updateHeader = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        headers: { ...prev.config.headers, [key]: value }
      }
    }));
  };

  const removeHeader = (key: string) => {
    setFormData(prev => {
      const newHeaders = { ...prev.config.headers };
      delete newHeaders[key];
      return {
        ...prev,
        config: { ...prev.config, headers: newHeaders }
      };
    });
  };

  const addHeader = () => {
    if (newHeaderKey && newHeaderValue) {
      updateHeader(newHeaderKey, newHeaderValue);
      setNewHeaderKey('');
      setNewHeaderValue('');
    }
  };

  const updateGradeMapping = (grade: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        grade_mapping: { ...prev.config.grade_mapping, [grade]: value }
      }
    }));
  };

  const canProceed = () => {
    if (currentStep === 0) {
      return formData.webhook_name.trim() !== '' && formData.webhook_url.trim() !== '';
    }
    return true;
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex <= currentStep || (stepIndex === currentStep + 1 && canProceed())) {
      setCurrentStep(stepIndex);
    }
  };

  // Render auth fields based on auth type
  const renderAuthFields = () => {
    const authType = formData.config.auth?.type || 'none';

    switch (authType) {
      case 'bearer':
        return (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bearer Token</label>
            <input
              type="text"
              value={formData.config.auth?.token || ''}
              onChange={(e) => updateAuth({ token: e.target.value })}
              placeholder="Enter bearer token"
              className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-brandColor font-mono"
            />
            <p className="mt-2 text-xs text-gray-500">Token will be sent as: Authorization: Bearer [token]</p>
          </div>
        );
      case 'basic':
        return (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username</label>
              <input
                type="text"
                value={formData.config.auth?.username || ''}
                onChange={(e) => updateAuth({ username: e.target.value })}
                placeholder="Username"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
              <input
                type="text"
                value={formData.config.auth?.password || ''}
                onChange={(e) => updateAuth({ password: e.target.value })}
                placeholder="Password"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
            </div>
          </div>
        );
      case 'api_key':
        return (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Header Name</label>
              <input
                type="text"
                value={formData.config.auth?.header_name || 'X-API-Key'}
                onChange={(e) => updateAuth({ header_name: e.target.value })}
                placeholder="X-API-Key"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">API Key</label>
              <input
                type="text"
                value={formData.config.auth?.api_key || ''}
                onChange={(e) => updateAuth({ api_key: e.target.value })}
                placeholder="Enter API key"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
            </div>
          </div>
        );
      case 'url_token':
        return (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Parameter Name</label>
              <input
                type="text"
                value={formData.config.auth?.param_name || 'token'}
                onChange={(e) => updateAuth({ param_name: e.target.value })}
                placeholder="token"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Token Value</label>
              <input
                type="text"
                value={formData.config.auth?.token || ''}
                onChange={(e) => updateAuth({ token: e.target.value })}
                placeholder="Enter token"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
            </div>
            <p className="col-span-2 text-xs text-gray-500">Token will be appended to URL as: ?{formData.config.auth?.param_name || 'token'}=[value]</p>
          </div>
        );
      case 'body_key':
        return (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Name</label>
              <input
                type="text"
                value={formData.config.auth?.key_name || 'secret_key'}
                onChange={(e) => updateAuth({ key_name: e.target.value })}
                placeholder="secret_key"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Value</label>
              <input
                type="text"
                value={formData.config.auth?.key_value || ''}
                onChange={(e) => updateAuth({ key_value: e.target.value })}
                placeholder="Enter secret key"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
            </div>
            <p className="col-span-2 text-xs text-gray-500">Key will be added to request body as: {formData.config.auth?.key_name || 'secret_key'}: [value]</p>
          </div>
        );
      case 'custom':
        return (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Add multiple custom headers as key-value pairs. These will be sent with every webhook request.
              </p>
            </div>

            {/* Existing headers */}
            {Object.keys(formData.config.headers || {}).length > 0 && (
              <div className="space-y-2">
                {Object.entries(formData.config.headers || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-sm font-mono min-w-[120px]">{key}</span>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateHeader(key, e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
                    />
                    <button
                      onClick={() => removeHeader(key)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new header */}
            <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <input
                  type="text"
                  value={newHeaderKey}
                  onChange={(e) => setNewHeaderKey(e.target.value)}
                  placeholder="Header name (e.g., X-Api-Key)"
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
                <input
                  type="text"
                  value={newHeaderValue}
                  onChange={(e) => setNewHeaderValue(e.target.value)}
                  placeholder="Header value"
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
                />
              </div>
              <button
                onClick={addHeader}
                disabled={!newHeaderKey || !newHeaderValue}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Header
              </button>
            </div>
          </div>
        );
      case 'zoho_oauth':
        return (
          <div className="mt-6 space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Zoho OAuth 2.0:</strong> Access tokens are automatically fetched using your refresh token before each webhook call.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Client ID <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.config.auth?.client_id || ''}
                  onChange={(e) => updateAuth({ client_id: e.target.value })}
                  placeholder="1000.XXXXX..."
                  className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Client Secret <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={formData.config.auth?.client_secret || ''}
                  onChange={(e) => updateAuth({ client_secret: e.target.value })}
                  placeholder="Enter client secret"
                  className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Refresh Token <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.config.auth?.refresh_token || ''}
                onChange={(e) => updateAuth({ refresh_token: e.target.value })}
                placeholder="1000.XXXXX..."
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
              <p className="mt-2 text-xs text-gray-500">Generate from Zoho API Console with offline_access scope</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Token URL</label>
              <input
                type="text"
                value={formData.config.auth?.token_url || 'https://accounts.zoho.in/oauth/v2/token'}
                onChange={(e) => updateAuth({ token_url: e.target.value })}
                placeholder="https://accounts.zoho.in/oauth/v2/token"
                className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor font-mono"
              />
              <p className="mt-2 text-xs text-gray-500">Use .com for US, .in for India, .eu for Europe</p>
            </div>
          </div>
        );
      default:
        return (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400">No authentication will be sent with webhook requests.</p>
          </div>
        );
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Webhook Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.webhook_name}
                onChange={(e) => setFormData({ ...formData, webhook_name: e.target.value })}
                placeholder="e.g., LeadSquared Integration"
                className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-2 text-sm text-gray-500">A friendly name to identify this webhook</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Webhook URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.webhook_url}
                onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                placeholder="https://api.example.com/webhook"
                className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-brandColor focus:border-brandColor dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-2 text-sm text-gray-500">The endpoint URL where lead data will be sent</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Webhook</label>
                <p className="text-sm text-gray-500 dark:text-gray-400">Activate to start sending leads immediately</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brandColor/20 dark:peer-focus:ring-brandColor/40 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-brandColor"></div>
              </label>
            </div>
          </div>
        );

      case 'auth':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Authentication Type</label>
              <div className="grid grid-cols-2 gap-3">
                {authTypes.map(type => (
                  <label
                    key={type.value}
                    className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.config.auth?.type === type.value
                        ? 'border-brandColor bg-brandColor/5 dark:bg-brandColor/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="auth_type"
                      value={type.value}
                      checked={formData.config.auth?.type === type.value}
                      onChange={(e) => updateAuth({ type: e.target.value as WebhookAuth['type'] })}
                      className="mt-1 text-brandColor focus:ring-brandColor"
                    />
                    <div>
                      <div className="font-medium text-sm text-gray-900 dark:text-white">{type.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{type.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {renderAuthFields()}
          </div>
        );

      case 'payload':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Select Payload Format</label>
              <div className="space-y-3">
                {payloadFormats.map(format => (
                  <label
                    key={format.value}
                    className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.config.payload_format === format.value
                        ? 'border-brandColor bg-brandColor/5 dark:bg-brandColor/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="payload_format"
                        value={format.value}
                        checked={formData.config.payload_format === format.value}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          config: { ...prev.config, payload_format: e.target.value as 'json' | 'leadsquared' | 'zoho_crm' | 'zoho_function' | 'custom' | 'form_data' }
                        }))}
                        className="mt-1 text-brandColor focus:ring-brandColor"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">{format.label}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{format.description}</div>
                        <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <code className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-all">{format.example}</code>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {formData.config.payload_format === 'custom' && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Template
                </label>
                <p className="text-sm text-gray-500 mb-3">Use {'{{field_name}}'} placeholders for dynamic values</p>
                <textarea
                  value={typeof formData.config.custom_template === 'string' ? formData.config.custom_template : JSON.stringify(formData.config.custom_template, null, 2)}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, custom_template: e.target.value }
                  }))}
                  placeholder={'{\n  "lead": {\n    "fullName": "{{name}}",\n    "email": "{{email}}",\n    "phone": "{{phone}}"\n  }\n}'}
                  rows={10}
                  className="w-full px-4 py-3 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
            )}

            {formData.config.payload_format === 'zoho_function' && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number Prefix
                </label>
                <p className="text-sm text-gray-500 mb-3">Country code to prepend to phone numbers (e.g., +91 for India)</p>
                <input
                  type="text"
                  value={formData.config.phone_prefix || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    config: { ...prev.config, phone_prefix: e.target.value }
                  }))}
                  placeholder="+91"
                  className="w-full px-4 py-3 text-sm border border-gray-300 dark:border-gray-600 rounded-xl dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
                <p className="mt-2 text-xs text-gray-500">Phone fields will be formatted as: {formData.config.phone_prefix || '+91'}9876543210</p>
              </div>
            )}
          </div>
        );

      case 'fields':
        return (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Left side:</strong> Your CRM field → <strong>Right side:</strong> External/Target field name (what the receiving system expects)
              </p>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">All Available Fields</h4>
                <span className="text-xs text-gray-500">{availableFields.length} fields</span>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {availableFields.map(field => (
                  <div key={field.field} className="flex items-center gap-4">
                    <div className="w-1/3">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</span>
                      <span className="block text-xs text-gray-500">{field.field}</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={formData.field_mapping[field.field] || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          field_mapping: { ...prev.field_mapping, [field.field]: e.target.value }
                        }))}
                        placeholder={field.field}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'grades':
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Convert grade values to match external system requirements. Leave empty to use default values.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {GRADE_KEYS.map(grade => (
                <div key={grade} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-1/3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{defaultGradeMappings[grade] || grade}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={formData.config.grade_mapping?.[grade] || ''}
                    onChange={(e) => updateGradeMapping(grade, e.target.value)}
                    placeholder={defaultGradeMappings[grade] || grade}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'static':
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add fixed values that will be included in every webhook request.
            </p>

            {/* Existing static fields */}
            {Object.entries(formData.config.static_fields || {}).length > 0 && (
              <div className="space-y-3">
                {Object.entries(formData.config.static_fields || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <input
                      type="text"
                      value={key}
                      disabled
                      className="w-1/3 px-3 py-2 text-sm bg-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-600 dark:text-white"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateStaticField(key, e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                    />
                    <button
                      onClick={() => removeStaticField(key)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new static field */}
            <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add New Field</h4>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newStaticKey}
                  onChange={(e) => setNewStaticKey(e.target.value)}
                  placeholder="Field name (e.g., source)"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
                <input
                  type="text"
                  value={newStaticValue}
                  onChange={(e) => setNewStaticValue(e.target.value)}
                  placeholder="Value (e.g., website)"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
                <button
                  onClick={addStaticField}
                  disabled={!newStaticKey || !newStaticValue}
                  className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Default Values Section */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Default Values</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Set default values for fields when the original value is empty or "N/A". Uses CRM field names (e.g., name, email, phone).
              </p>

              {/* Existing default values */}
              {Object.entries(formData.config.default_values || {}).length > 0 && (
                <div className="space-y-3 mb-4">
                  {Object.entries(formData.config.default_values || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                      <input
                        type="text"
                        value={key}
                        disabled
                        className="w-1/3 px-3 py-2 text-sm bg-amber-100 border border-amber-300 dark:border-amber-700 rounded-lg dark:bg-amber-800/50 dark:text-white"
                      />
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateDefaultValue(key, e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-amber-500"
                      />
                      <button
                        onClick={() => removeDefaultValue(key)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new default value */}
              <div className="p-4 border-2 border-dashed border-amber-300 dark:border-amber-700 rounded-xl bg-amber-50/50 dark:bg-amber-900/10">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add Default Value</h4>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newDefaultKey}
                    onChange={(e) => setNewDefaultKey(e.target.value)}
                    placeholder="CRM field (e.g., name)"
                    className="flex-1 px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                  <input
                    type="text"
                    value={newDefaultValue}
                    onChange={(e) => setNewDefaultValue(e.target.value)}
                    placeholder="Default value (e.g., Lead)"
                    className="flex-1 px-3 py-2 text-sm border border-amber-300 dark:border-amber-700 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    onClick={addDefaultValue}
                    disabled={!newDefaultKey || !newDefaultValue}
                    className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  Used when field value is empty, "N/A", or null
                </p>
              </div>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            {/* Basic Info Summary */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <WebhookIcon className="h-4 w-4" />
                Basic Information
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.webhook_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">URL:</span>
                  <span className="font-medium text-gray-900 dark:text-white truncate max-w-xs">{formData.webhook_url}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span className={`font-medium ${formData.is_active ? 'text-green-600' : 'text-gray-500'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Auth Summary */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Key className="h-4 w-4" />
                Authentication
              </h4>
              <p className="text-sm text-gray-900 dark:text-white">
                {authTypes.find(t => t.value === formData.config.auth?.type)?.label || 'No Authentication'}
              </p>
            </div>

            {/* Payload Summary */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                Payload Format
              </h4>
              <p className="text-sm text-gray-900 dark:text-white">
                {payloadFormats.find(f => f.value === formData.config.payload_format)?.label || 'Standard JSON'}
              </p>
            </div>

            {/* Field Mapping Summary */}
            {Object.keys(formData.field_mapping).length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Field Mappings
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(formData.field_mapping).filter(([_, v]) => v).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs">
                      {key} → {value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Grade Mapping Summary */}
            {Object.keys(formData.config.grade_mapping || {}).filter(k => formData.config.grade_mapping?.[k]).length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Grade Mappings
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(formData.config.grade_mapping || {}).filter(([_, v]) => v).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs">
                      {defaultGradeMappings[key] || key} → {value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Static Fields Summary */}
            {Object.keys(formData.config.static_fields || {}).length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Static Fields
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(formData.config.static_fields || {}).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs">
                      {key}: {value}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Default Values Summary */}
            {Object.keys(formData.config.default_values || {}).length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Default Values (for empty/N/A fields)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(formData.config.default_values || {}).map(([key, value]) => (
                    <span key={key} className="px-2 py-1 bg-white dark:bg-gray-700 border border-amber-300 dark:border-amber-700 rounded text-xs">
                      {key} → {value}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Full-screen Wizard UI
  if (showWizard) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleCloseWizard}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedWebhook ? 'Edit Webhook' : 'Create Webhook'}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {STEPS[currentStep].title} - Step {currentStep + 1} of {STEPS.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                const isClickable = index <= currentStep || (index === currentStep + 1 && canProceed());

                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(index)}
                    disabled={!isClickable}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-brandColor text-white'
                        : isCompleted
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : isClickable
                            ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                            : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      isActive
                        ? 'bg-white/20'
                        : isCompleted
                          ? 'bg-green-200 dark:bg-green-800'
                          : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <StepIcon className="h-4 w-4" />
                      )}
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-medium">{step.title}</div>
                      <div className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-500'}`}>{step.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{STEPS[currentStep].title}</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{STEPS[currentStep].description}</p>
            </div>
            {renderStepContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleCloseWizard}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Cancel
                </button>

                {currentStep === STEPS.length - 1 ? (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                  >
                    {saving ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {selectedWebhook ? 'Update Webhook' : 'Create Webhook'}
                  </button>
                ) : (
                  <button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main webhooks list view
  return (
    <>
      {/* Header */}
      <ModernSettingHeader
        title="Webhooks"
        description="Send lead data to external services in real-time"
        icon={<WebhookIcon className="h-5 w-5" />}
        buttonText="Add Webhook"
        onClick={() => handleOpenWizard()}
      />

      <div className="p-6">
        {/* LeadSquared Inbound Section */}
        <LeadSquaredInboundCard />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-brandColor" />
          </div>
        ) : webhooks.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Activity className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No webhooks configured</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first webhook to send lead data to external services
            </p>
            <button
              onClick={() => handleOpenWizard()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover"
            >
              <Plus className="h-4 w-4" />
              Add Webhook
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {webhook.webhook_name}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        webhook.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {webhook.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {webhook.config?.payload_format && webhook.config.payload_format !== 'json' && (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {webhook.config.payload_format === 'leadsquared' ? 'LeadSquared' : 'Custom'}
                        </span>
                      )}
                      {webhook.config?.auth?.type && webhook.config.auth.type !== 'none' && (
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          {webhook.config.auth.type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <ExternalLink className="h-4 w-4" />
                      <span className="truncate max-w-md">{webhook.webhook_url}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span>Created {new Date(webhook.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(webhook)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title={webhook.is_active ? 'Disable' : 'Enable'}
                    >
                      {webhook.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleViewStats(webhook)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title="View stats"
                    >
                      <BarChart3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleViewLogs(webhook)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title="View logs"
                    >
                      <Activity className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleOpenWizard(webhook)}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedWebhook(webhook);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logs Drawer */}
      <SettingsSlideModal
        isOpen={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        title="Activity Logs"
        subtitle={selectedWebhook ? `Webhook delivery history for ${selectedWebhook.webhook_name}` : 'View webhook activity'}
        icon={<Activity className="w-6 h-6" />}
        width="900px"
      >
        <div className="flex flex-col h-full min-h-[400px]">
          {/* Search and Date Filter */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
            {/* Status Filter Chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status:</span>
              <button
                onClick={() => { setLogsStatusFilter(''); handleFilterLogs(''); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  logsStatusFilter === ''
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              <button
                onClick={() => { setLogsStatusFilter('success'); handleFilterLogs('success'); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
                  logsStatusFilter === 'success'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                }`}
              >
                <Check className="h-3 w-3" />
                Success
              </button>
              <button
                onClick={() => { setLogsStatusFilter('duplicate'); handleFilterLogs('duplicate'); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
                  logsStatusFilter === 'duplicate'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                }`}
              >
                <AlertCircle className="h-3 w-3" />
                Duplicate
              </button>
              <button
                onClick={() => { setLogsStatusFilter('api_error'); handleFilterLogs('api_error'); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
                  logsStatusFilter === 'api_error'
                    ? 'bg-red-600 text-white'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                }`}
              >
                <X className="h-3 w-3" />
                API Error
              </button>
              <button
                onClick={() => { setLogsStatusFilter('failed'); handleFilterLogs('failed'); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex items-center gap-1 ${
                  logsStatusFilter === 'failed'
                    ? 'bg-gray-700 text-white dark:bg-gray-500'
                    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <AlertCircle className="h-3 w-3" />
                Network Error
              </button>
            </div>

            {/* Search and Date */}
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Search className="h-3 w-3 inline mr-1" />
                  Search
                </label>
                <input
                  type="text"
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFilterLogs()}
                  placeholder="Search by phone, name, email..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={logsStartDate}
                  onChange={(e) => setLogsStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
              <div className="min-w-[140px]">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={logsEndDate}
                  onChange={(e) => setLogsEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
              <button
                onClick={() => handleFilterLogs()}
                disabled={logsLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2"
              >
                {logsLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Search
              </button>
              {(logsStartDate || logsEndDate || logsSearch || logsStatusFilter) && (
                <button
                  onClick={() => {
                    setLogsStartDate('');
                    setLogsEndDate('');
                    setLogsSearch('');
                    setLogsStatusFilter('');
                    if (selectedWebhook) {
                      handleViewLogs(selectedWebhook);
                    }
                  }}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {logsLoading && logs.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 animate-spin text-brandColor" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No activity logs yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => {
                  const name = log.request_payload?.FirstName || log.request_payload?.name || log.request_payload?.data?.name || '-';
                  const phone = log.request_payload?.MobileNumber || log.request_payload?.phone || log.request_payload?.data?.phone || '-';
                  const email = log.request_payload?.Email || log.request_payload?.email || log.request_payload?.data?.email || '-';

                  // Determine actual success/failure from response
                  const responseResult = log.response_data?.result ?? log.response_data?.status ?? log.response_data?.Status;
                  const responseMessage = log.response_data?.message || log.response_data?.Message || log.response_data?.error || log.response_data?.Error || '';
                  const responseResultStr = typeof responseResult === 'string' ? responseResult.toLowerCase() : '';
                  const isApiSuccess = log.status === 'sent' && (!responseResult || responseResultStr === 'success' || responseResult === true || responseResult === 200);
                  const isApiDuplicate = responseMessage?.toLowerCase().includes('already exists') || log.response_data?.leadAlreadyExists === true || log.response_data?.isLeadExists === true;

                  return (
                    <div
                      key={log.id}
                      className={`border rounded-lg overflow-hidden ${
                        log.status === 'failed'
                          ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                          : isApiSuccess && !isApiDuplicate
                            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                            : 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10'
                      }`}
                    >
                      {/* Main Row */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          {/* Lead Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 dark:text-white truncate">{name}</span>
                              {log.status_code && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">HTTP {log.status_code}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <span>{phone}</span>
                              {email && email !== '-' && email !== 'null' && (
                                <span className="truncate">{email}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {new Date(log.sent_at).toLocaleString()}
                            </div>
                          </div>

                          {/* Status */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {log.status === 'failed' ? (
                              <>
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                  <X className="h-3 w-3" />
                                  Failed
                                </span>
                                <button
                                  onClick={() => handleResendWebhook(log.id)}
                                  disabled={resendingLogId === log.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                                  title="Resend webhook"
                                >
                                  {resendingLogId === log.id ? (
                                    <Loader className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3 w-3" />
                                  )}
                                  Resend
                                </button>
                              </>
                            ) : isApiSuccess && !isApiDuplicate ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                <Check className="h-3 w-3" />
                                Success
                              </span>
                            ) : isApiDuplicate ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                <AlertCircle className="h-3 w-3" />
                                Duplicate
                              </span>
                            ) : (
                              <>
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                  <X className="h-3 w-3" />
                                  API Error
                                </span>
                                <button
                                  onClick={() => handleResendWebhook(log.id)}
                                  disabled={resendingLogId === log.id}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                                  title="Resend webhook"
                                >
                                  {resendingLogId === log.id ? (
                                    <Loader className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3 w-3" />
                                  )}
                                  Resend
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Response Message */}
                        {(log.error_message || responseMessage) && (
                          <div className={`mt-3 p-2 rounded text-sm ${
                            log.status === 'failed' || (responseResult && responseResultStr === 'failed')
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                              : isApiDuplicate
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }`}>
                            <span className="font-medium">Response: </span>
                            {log.error_message || responseMessage}
                          </div>
                        )}

                        {/* Expandable Details */}
                        <details className="mt-3">
                          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                            View full request/response
                          </summary>
                          <div className="mt-2 grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Request Payload</div>
                              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto max-h-48 text-gray-600 dark:text-gray-400">
                                {JSON.stringify(log.request_payload, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Response Data</div>
                              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto max-h-48 text-gray-600 dark:text-gray-400">
                                {log.response_data ? JSON.stringify(log.response_data, null, 2) : 'No response'}
                              </pre>
                            </div>
                          </div>
                        </details>
                      </div>
                    </div>
                  );
                })}

                {/* Load More Section */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Showing {logs.length} of {logsTotal} logs
                  </div>
                  {logsHasMore && (
                    <div className="text-center">
                      <button
                        onClick={handleLoadMoreLogs}
                        disabled={logsLoading}
                        className="px-6 py-2 text-sm font-medium text-brandColor border border-brandColor rounded-lg hover:bg-brandColor hover:text-white disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                      >
                        {logsLoading ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" />
                            Load More
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </SettingsSlideModal>

      {/* Delete Confirmation */}
      <SettingsSlideModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Webhook"
        subtitle={selectedWebhook ? `Permanently remove ${selectedWebhook.webhook_name}` : 'Confirm deletion'}
        icon={<AlertCircle className="w-6 h-6 text-red-600" />}
        width="448px"
      >
        <div className="flex flex-col h-full min-h-[200px]">
          <div className="flex-1 overflow-y-auto p-6">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Are you sure you want to delete <span className="font-semibold">"{selectedWebhook?.webhook_name}"</span>?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Webhook
              </button>
            </div>
          </div>
        </div>
      </SettingsSlideModal>

      {/* Stats Modal */}
      <SettingsSlideModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        title="Webhook Report"
        subtitle={selectedWebhook ? `Delivery statistics for ${selectedWebhook.webhook_name}` : 'View webhook statistics'}
        icon={<BarChart3 className="w-6 h-6" />}
        width="500px"
      >
        <div className="flex flex-col h-full min-h-[400px]">
          {/* Date Filter */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={statsStartDate}
                  onChange={(e) => setStatsStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={statsEndDate}
                  onChange={(e) => setStatsEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-brandColor"
                />
              </div>
              <button
                onClick={handleFilterStats}
                disabled={statsLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:bg-brand-hover disabled:opacity-50 flex items-center gap-2"
              >
                {statsLoading ? <Loader className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Apply
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-8 w-8 animate-spin text-brandColor" />
              </div>
            ) : stats ? (
              <div className="space-y-6">
                {/* Total */}
                <div className="text-center p-4 bg-gray-100 dark:bg-gray-800 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Deliveries</div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Success */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">Success</span>
                    </div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.success}</div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      {stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0}% of total
                    </div>
                  </div>

                  {/* Duplicate */}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Duplicate</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.duplicate}</div>
                    <div className="text-xs text-amber-600 dark:text-amber-400">
                      {stats.total > 0 ? Math.round((stats.duplicate / stats.total) * 100) : 0}% of total
                    </div>
                  </div>

                  {/* API Error */}
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <X className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <span className="text-sm font-medium text-red-700 dark:text-red-300">API Error</span>
                    </div>
                    <div className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.apiError}</div>
                    <div className="text-xs text-red-600 dark:text-red-400">
                      {stats.total > 0 ? Math.round((stats.apiError / stats.total) * 100) : 0}% of total
                    </div>
                  </div>

                  {/* Network Error */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Network Error</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{stats.networkError}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {stats.total > 0 ? Math.round((stats.networkError / stats.total) * 100) : 0}% of total
                    </div>
                  </div>
                </div>

                {/* Success Rate Bar */}
                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Success Rate</span>
                    <span className="text-sm font-bold text-brandColor">{stats.successRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className="bg-brandColor h-3 rounded-full transition-all duration-500"
                      style={{ width: `${stats.successRate}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No statistics available</p>
              </div>
            )}
          </div>
        </div>
      </SettingsSlideModal>
    </>
  );
}
